import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Menu, RadioBox } from 'lego-on-react';
import { difference, partialRight } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { getAllSelectableChooserItemsLength } from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { TestBem } from '../../service/TestBem';
import {
    RateCorrectionConditionChooserItem,
    RateCorrectionConditionChooserItemsGroup
} from '../../typings/ratesCorrections';
import { CheckboxDecorator } from '../CheckboxDecorator/CheckboxDecorator';
import {
    HoverTooltipWrapper,
    HoverTooltipWrapperTheme
} from '../HoverTooltipWrapper/HoverTooltipWrapper';
import { MarkedText } from '../MarkedText/MarkedText';
import { TextInputDecorator } from '../TextInputDecorator/TextInputDecorator';

import './RateCorrectionConditionChooser.css';
import * as keyset from './RateCorrectionConditionChooser.i18n';

const i18n = i18nFactory(keyset);

export enum RateCorrectionConditionChooserView {
    default = 'default',
    withCheckers = 'withCheckers'
}

export interface RateCorrectionConditionChooserSearchFilterParams {
    isEnabled: boolean;
    labelSearch?: string;
}

export interface RateCorrectionConditionChooserProps {
    cls?: string;
    items: Array<RateCorrectionConditionChooserItemsGroup>;
    value: Array<string>;
    disabledItems?: Array<string>;
    hideDisabledItems?: boolean;
    checkAllControl?: {
        label: string;
    };
    view: RateCorrectionConditionChooserView;
    multiSelect: boolean;
    searchFilter?: RateCorrectionConditionChooserSearchFilterParams;
    maxSelectableItemsNumber?: number;
    onChange: (values: Array<string>) => void;
}

export interface RateCorrectionConditionChooserState {
    filter: string;
}

type ClickMenuItemEventHandler = (
    event: React.MouseEvent<HTMLElement>,
    val: string
) => void;

const blockClass = 'rate-correction-condition-chooser';

export class RateCorrectionConditionChooser extends React.PureComponent<
    RateCorrectionConditionChooserProps,
    RateCorrectionConditionChooserState
> {
    constructor(props: RateCorrectionConditionChooserProps) {
        super(props);

        this.state = {
            filter: ''
        };
    }

    render() {
        const { items, disabledItems = [], hideDisabledItems } = this.props;
        const { filter } = this.state;
        let itemsToShowLength = 0;

        const menuContent = items.map((menuGroup, index) => {
            const hasMultiChoice = menuGroup.hasMultiChoice;
            const onClickHandler: ClickMenuItemEventHandler = partialRight(
                this.onMenuItemClick,
                index,
                hasMultiChoice
            );
            let itemsToShow = menuGroup.items;

            if (hideDisabledItems) {
                itemsToShow = itemsToShow.filter(
                    item => !disabledItems.includes(item.id)
                );
            }

            if (filter !== '') {
                itemsToShow = itemsToShow.filter(
                    item =>
                        item.name
                            .toLocaleLowerCase()
                            .indexOf(filter.toLocaleLowerCase()) !== -1
                );
            }

            itemsToShowLength += itemsToShow.length;

            return (
                <Menu.Group key={index} title={menuGroup.name}>
                    {itemsToShow.map(menuItem =>
                        this.getMenuItem(
                            menuItem,
                            index,
                            hasMultiChoice,
                            onClickHandler
                        )
                    )}
                </Menu.Group>
            );
        });

        return (
            <div className={Bem.cls(blockClass)}>
                {this.getHeader()}

                {itemsToShowLength === 0 ? (
                    this.getEmptyMessage()
                ) : (
                    <Menu
                        cls={Bem.cls(blockClass, 'menu')}
                        theme={'normal'}
                        size={'s'}
                        type={'default'}
                    >
                        {menuContent}
                    </Menu>
                )}
            </div>
        );
    }

    private getHeader = () => {
        const {
            value,
            checkAllControl,
            items,
            disabledItems = []
        } = this.props;
        let checkAllControlContent = null;
        const searchFilterContent = this.getSearchFilter();
        const selectableItemsHintContent = this.getSelectableItemsNumberHint();

        if (checkAllControl && this.hasCheckAllControl()) {
            const allItemsIdsLength = getAllSelectableChooserItemsLength(items);

            checkAllControlContent = (
                <CheckboxDecorator
                    cls={Bem.cls(blockClass, 'all-checker')}
                    size="s"
                    theme="normal"
                    checked={
                        value.length ===
                        allItemsIdsLength - disabledItems.length
                    }
                    text={checkAllControl.label}
                    onChecked={this.onCheckAllControlClick}
                />
            );
        }

        if (
            !checkAllControlContent &&
            !searchFilterContent &&
            !selectableItemsHintContent
        ) {
            return null;
        }

        return (
            <div
                className={Bem.cls({
                    block: blockClass,
                    elem: 'header',
                    mods: { 'has-checker': Boolean(checkAllControlContent) }
                })}
            >
                {searchFilterContent}

                {checkAllControlContent}

                {this.getSelectableItemsNumberHint()}
            </div>
        );
    };

    private hasCheckAllControl = () => {
        const { checkAllControl, items } = this.props;

        return checkAllControl && items.every(item => item.hasMultiChoice);
    };

    private getSelectableItemsNumberHint = () => {
        const { maxSelectableItemsNumber, value, items } = this.props;
        const hasCheckAllControl = this.hasCheckAllControl();
        const allSelectableItems = getAllSelectableChooserItemsLength(items);

        if (
            maxSelectableItemsNumber === undefined ||
            allSelectableItems <= maxSelectableItemsNumber
        ) {
            return null;
        }

        let content;

        if (maxSelectableItemsNumber === value.length) {
            content = i18n('max-items-number-selected');
        } else if (maxSelectableItemsNumber < value.length) {
            content = i18n('more-than-max-items-number-selected', {
                count: maxSelectableItemsNumber
            });
        } else if (hasCheckAllControl) {
            content = i18n('first-n-items-will-be-selected', {
                count: maxSelectableItemsNumber
            });
        }

        return (
            <div className={Bem.cls(blockClass, 'header-hint')}>{content}</div>
        );
    };

    private getEmptyMessage = (): React.ReactNode => {
        const { searchFilter } = this.props;

        if (searchFilter === undefined) {
            return null;
        }

        return (
            <div className={Bem.cls(blockClass, 'empty-message')}>
                <h3 className={Bem.cls(blockClass, 'empty-message-title')}>
                    {i18n('no-results-title')}
                </h3>
                <p className={Bem.cls(blockClass, 'empty-message-text')}>
                    {i18n('no-results-text')}
                </p>
            </div>
        );
    };

    private getSearchFilter = () => {
        const { searchFilter } = this.props;
        const { filter } = this.state;

        if (searchFilter === undefined || !searchFilter.isEnabled) {
            return null;
        }

        return (
            <div className={Bem.cls(blockClass, 'search-wrap')}>
                <TextInputDecorator
                    hasClear={true}
                    cls={Bem.cls(blockClass, 'search')}
                    placeholder={searchFilter.labelSearch || ''}
                    onChange={this.onFilterChange}
                    size={'s'}
                    theme={'normal'}
                    text={filter}
                />
            </div>
        );
    };

    private onCheckAllControlClick = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean }
    ) => {
        const {
            items,
            disabledItems = [],
            onChange,
            maxSelectableItemsNumber,
            value
        } = this.props;
        const moreThanMaxItemsNumberSelected =
            maxSelectableItemsNumber &&
            value.length >= maxSelectableItemsNumber;

        if (moreThanMaxItemsNumberSelected) {
            onChange([]);
        } else {
            const itemsIdsToChange = items.reduce(
                (
                    res: Array<string>,
                    group: RateCorrectionConditionChooserItemsGroup
                ) => {
                    group.items.forEach(item => {
                        if (!disabledItems.includes(item.id)) {
                            res.push(item.id);
                        }
                    });

                    return res;
                },
                []
            );

            onChange(
                maxSelectableItemsNumber
                    ? itemsIdsToChange.slice(0, maxSelectableItemsNumber)
                    : itemsIdsToChange
            );
        }
    };

    private getMenuItem = (
        { id, name, hint }: RateCorrectionConditionChooserItem,
        groupIndex: number,
        hasMultiChoice: boolean,
        onClick: ClickMenuItemEventHandler
    ) => {
        const { view, value, disabledItems = [] } = this.props;
        const isChecked = value.includes(id);
        const isDisabled = disabledItems.includes(id);
        const { filter } = this.state;
        let checker;

        if (view === RateCorrectionConditionChooserView.withCheckers) {
            checker = hasMultiChoice ? (
                <CheckboxDecorator
                    cls={Bem.cls(blockClass, 'checker')}
                    size="s"
                    theme="normal"
                    checked={isChecked}
                    disabled={isDisabled}
                />
            ) : (
                <RadioBox
                    size="s"
                    theme="normal"
                    cls={Bem.cls(blockClass, 'checker')}
                    disabled={isDisabled}
                >
                    <RadioBox.Radio checked={isChecked} />
                </RadioBox>
            );
        } else {
            checker = null;
        }

        const menuItemContent = (
            <div
                className={classNames(
                    Bem.cls(blockClass, 'menu-item'),
                    TestBem.cls(blockClass, 'menu-item', {
                        id
                    })
                )}
            >
                {checker}

                <div
                    className={Bem.cls(blockClass, 'menu-item-text')}
                    title={name}
                >
                    <MarkedText text={name} search={filter} />
                </div>
            </div>
        );

        return (
            <Menu.Item
                key={id}
                val={id}
                // DIRECT-89865: в тайпингах обработчика лего известно только про  event, на самом деле есть еще value
                onClick={
                    onClick as (event: React.MouseEvent<HTMLElement>) => void
                }
                disabled={isDisabled}
            >
                {hint === undefined ? (
                    menuItemContent
                ) : (
                    <HoverTooltipWrapper
                        cls={Bem.cls(blockClass, 'warning-tooltip')}
                        theme={'normal'}
                        themeWrapper={HoverTooltipWrapperTheme.clear}
                        view={'classic'}
                        to={['right']}
                        size={'s'}
                        tooltipText={hint}
                    >
                        {menuItemContent}
                    </HoverTooltipWrapper>
                )}
            </Menu.Item>
        );
    };

    private onMenuItemClick = (
        event: React.MouseEvent<HTMLElement>,
        val: string,
        groupIndex: number,
        groupHasMultiChoice: boolean
    ) => {
        const { value, onChange, items, multiSelect } = this.props;
        let newValues = value.concat();

        if (multiSelect) {
            if (groupHasMultiChoice) {
                const valueIndex = newValues.indexOf(val);

                valueIndex === -1
                    ? newValues.push(val)
                    : newValues.splice(valueIndex, 1);
            } else {
                const groupValues = items[groupIndex].items.map(
                    groupItem => groupItem.id
                );

                newValues = difference(newValues, groupValues);
                newValues.push(val);
            }
        } else {
            newValues = [val];
        }

        onChange(newValues);
    };

    private onFilterChange = (value: string) => {
        this.setState({
            filter: value
        });
    };
}
